import { FormLeaveCancelledError, type PortalFormSessionManager } from './formSessionManager';
import { portalFormSessionError } from '@jukun/platform-sdk/core';

const HISTORY_KEY = '__portalFormNavigation';
type Location = Readonly<{ path: string; index: number }>;
export type FormNavigationTransaction = Readonly<{ id: number; target: string; isCurrent: () => boolean; claim: () => boolean }>;
export type FormNavigationPreparation = (transaction: FormNavigationTransaction) => Promise<boolean>;

/** Keeps the displayed route separate from the browser's pending back/forward location. */
export class FormNavigationGuard {
  private generation = 0;
  private nextId = 0;
  private committed: Location;
  private restoring?: { location: Location; resolve: () => void; promise: Promise<void> };
  private revokedRecovery?: Location;
  constructor(private readonly options: {
    manager: () => PortalFormSessionManager | undefined;
    history: Pick<History, 'state' | 'pushState' | 'replaceState' | 'go'>;
    location: () => string;
    commit: (path: string) => void;
    onError: (error: unknown) => void;
  }) {
    this.committed = { path: options.location(), index: this.readIndex(options.history.state) ?? 0 };
    this.write('replace', this.committed);
  }

  private readIndex(state: unknown): number | undefined {
    const value = state && typeof state === 'object' ? Reflect.get(state, HISTORY_KEY) : undefined;
    return typeof value === 'number' && Number.isSafeInteger(value) ? value : undefined;
  }
  private write(mode: 'push' | 'replace', location: Location) {
    const prior = this.options.history.state;
    const state = { ...(prior && typeof prior === 'object' ? prior : {}), [HISTORY_KEY]: location.index };
    if (mode === 'push') this.options.history.pushState(state, '', location.path);
    else this.options.history.replaceState(state, '', location.path);
  }

  async leave(transaction: FormNavigationTransaction): Promise<boolean> {
    if (!transaction.claim()) return false;
    const manager = this.options.manager();
    if (!manager) return transaction.isCurrent();
    const permit = await manager.requestLeave(transaction.target);
    if (!transaction.isCurrent()) return false;
    if (permit.status !== 'ALLOW' || !manager.isLeavePermitCurrent(permit)) {
      if (permit.status === 'BUSY' || permit.status === 'STATE_UNAVAILABLE' || permit.status === 'LEAVE_CHANGED')
        this.options.onError(portalFormSessionError(permit.status === 'BUSY'
          ? 'PORTAL_FORM_SESSION_SWITCH_BUSY' : 'PORTAL_FORM_SESSION_LEAVE_CHANGED'));
      return false;
    }
    const status = await manager.commitLeave(permit);
    return status === 'ALLOW' && transaction.isCurrent();
  }

  navigate(target: string, prepare?: FormNavigationPreparation, replace = false) {
    return this.run(target, prepare, replace ? 'replace' : 'push');
  }

  handlePopState(state: unknown, prepare?: FormNavigationPreparation): Promise<boolean> {
    const target = this.options.location();
    const index = this.readIndex(state);
    if (this.revokedRecovery && target === this.revokedRecovery.path && index === this.revokedRecovery.index) {
      this.revokedRecovery = undefined;
      this.write('replace', this.committed);
      return Promise.resolve(false);
    }
    const restore = this.restoring;
    if (restore && target === restore.location.path && index === restore.location.index) {
      this.restoring = undefined; restore.resolve();
      return Promise.resolve(false); // Consume our owned recovery event, never prompt twice.
    }
    return this.run(target, prepare, 'pop', index);
  }

  /** Only confirmed authentication invalidation uses this synchronous route adoption. */
  adoptForcedLocation() {
    this.generation = ++this.nextId;
    this.revokedRecovery = this.restoring?.location;
    this.restoring?.resolve(); this.restoring = undefined;
    this.committed = { path: this.options.location(), index: this.readIndex(this.options.history.state) ?? this.committed.index };
    this.write('replace', this.committed); this.options.commit(this.committed.path);
  }

  private async run(target: string, prepare: FormNavigationPreparation | undefined,
    mode: 'push' | 'replace' | 'pop', targetIndex?: number): Promise<boolean> {
    const base = this.generation;
    const id = ++this.nextId;
    const source = this.committed;
    let claimed = false;
    const transaction = Object.freeze({ id, target,
      isCurrent: () => this.generation === (claimed ? id : base),
      claim: () => {
        if (this.generation !== (claimed ? id : base)) return false;
        claimed = true; this.generation = id; return true;
      },
    });
    try {
      // Await recovery before a new push, so a late history.go cannot overwrite its target.
      if (this.restoring) await this.restoring.promise;
      if (!transaction.isCurrent()) return false;
      // A programmatic intent may supersede a pending native history move. Recover the
      // displayed source before its push, instead of branching from an unapproved entry.
      if (mode !== 'pop' && this.options.location() !== source.path) {
        this.restore(source, this.readIndex(this.options.history.state));
        if (this.restoring) await this.restoring.promise;
        if (!transaction.isCurrent()) return false;
      }
      const allowed = await (prepare ? prepare(transaction) : this.leave(transaction));
      if (this.restoring) await this.restoring.promise;
      if (!transaction.isCurrent()) return false;
      if (!allowed) {
        this.restore(source, this.readIndex(this.options.history.state));
        return false;
      }
      if (!transaction.claim()) return false;
      const index = mode === 'pop' ? targetIndex ?? source.index
        : mode === 'replace' || target === source.path ? source.index : source.index + 1;
      const next = { path: target, index };
      // A previous owned recovery may have moved the browser while this newer native
      // intent waited. Replay its exact indexed entry before committing its React page.
      if (mode === 'pop' && this.options.location() !== target) {
        this.restore(next, this.readIndex(this.options.history.state));
        if (this.restoring) await this.restoring.promise;
        if (!transaction.isCurrent()) return false;
      }
      if (mode !== 'pop') this.write(mode === 'push' && target !== source.path ? 'push' : 'replace', next);
      else if (targetIndex === undefined) this.write('replace', next);
      this.committed = next;
      this.options.commit(target);
      return true;
    } catch (error) {
      if (transaction.isCurrent()) {
        this.restore(source, this.readIndex(this.options.history.state));
        if (!(error instanceof FormLeaveCancelledError)) this.options.onError(error);
      }
      return false;
    }
  }

  private restore(source: Location, targetIndex?: number) {
    if (this.options.location() === source.path) return;
    if (targetIndex !== undefined && targetIndex !== source.index) {
      let resolve!: () => void;
      const promise = new Promise<void>(done => { resolve = done; });
      this.restoring = { location: source, resolve, promise };
      this.options.history.go(source.index - targetIndex);
    } else this.write('replace', source);
  }
}
